import { useState } from 'react';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import {
  type AgentLogEntry,
  type AgentToolActionEntry,
  useAgentExecutionDetail,
  useAgentLogs,
} from '../hooks/useAdminData';

const FALLBACK_MODELS = new Set(['rule-based-fallback', 'rule-based-v1']);

function statusVariant(status: string): 'success' | 'danger' | 'info' | 'default' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running' || status === 'queued') return 'info';
  return 'default';
}

function isFallback(entry: AgentLogEntry): boolean {
  return !!entry.modelUsed && FALLBACK_MODELS.has(entry.modelUsed);
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

// A tool row used to show only status + name, which told an operator that a
// tool ran but not what it did. The detail endpoint already returns the
// parameters it was called with and the result it returned — expand to show them.
function ToolActionRow({ action }: { action: AgentToolActionEntry }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li>
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 text-left hover:bg-surface-hover rounded px-1 py-0.5"
      >
        <Badge variant={statusVariant(action.status)}>{action.status}</Badge>
        <span className="text-text font-mono">{action.toolName}</span>
        {action.durationMs !== null ? (
          <span className="text-text-muted text-xs">{action.durationMs}ms</span>
        ) : null}
        {action.errorMessage ? (
          <span className="text-danger">— {action.errorMessage}</span>
        ) : null}
      </button>

      {expanded ? (
        <div className="mt-1 ml-1 space-y-2">
          <div>
            <div className="text-text-muted text-xs uppercase tracking-wide">Parameters</div>
            <pre className="text-xs bg-surface-alt rounded p-2 overflow-x-auto">
              {formatJson(action.parameters)}
            </pre>
          </div>
          <div>
            <div className="text-text-muted text-xs uppercase tracking-wide">Result</div>
            <pre className="text-xs bg-surface-alt rounded p-2 overflow-x-auto">
              {formatJson(action.result)}
            </pre>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function ExecutionDetail({ id }: { id: string }) {
  const { data, isLoading } = useAgentExecutionDetail(id);

  if (isLoading) return <div className="text-text-secondary">Loading detail…</div>;
  if (!data) return <div className="text-text-secondary">No detail available.</div>;

  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap gap-2">
        <Badge variant={statusVariant(data.status)}>{data.status}</Badge>
        {isFallback(data) ? (
          <Badge variant="warning">fallback: {data.modelUsed}</Badge>
        ) : (
          <Badge variant="default">{data.modelUsed ?? 'n/a'}</Badge>
        )}
        <Badge variant="info">{data.triggeredBy ?? 'unknown trigger'}</Badge>
      </div>

      {data.errorMessage ? (
        <div>
          <div className="font-semibold text-danger">Error</div>
          <pre className="overflow-x-auto rounded bg-background p-3 text-danger">
            {data.errorMessage}
          </pre>
        </div>
      ) : null}

      <div>
        <div className="font-semibold text-text">Output</div>
        <pre className="overflow-x-auto rounded bg-background p-3 text-text-secondary">
          {formatJson(data.output)}
        </pre>
      </div>

      <div>
        <div className="font-semibold text-text">Input</div>
        <pre className="overflow-x-auto rounded bg-background p-3 text-text-secondary">
          {formatJson(data.input)}
        </pre>
      </div>

      {data.toolActions.length > 0 ? (
        <div>
          <div className="font-semibold text-text">Tool actions ({data.toolActions.length})</div>
          <ul className="space-y-1">
            {data.toolActions.map((ta) => (
              <ToolActionRow key={ta.id} action={ta} />
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function LeadAgentTimeline() {
  const [leadIdInput, setLeadIdInput] = useState('');
  const [leadId, setLeadId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isError } = useAgentLogs(
    leadId ? { leadId, limit: 100 } : undefined,
  );

  const executions = leadId ? (data?.data ?? []) : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-text">Lead Agent Timeline</h1>
        <p className="text-text-secondary">
          Follow every AI agent execution for a single lead — spot rule-based fallbacks and failures
          at a glance.
        </p>
      </div>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setLeadId(leadIdInput.trim() || null);
        }}
      >
        <input
          aria-label="Lead ID"
          className="flex-1 rounded border border-border bg-surface px-3 py-2 text-text"
          placeholder="Paste a lead ID…"
          value={leadIdInput}
          onChange={(e) => setLeadIdInput(e.target.value)}
        />
        <button
          type="submit"
          className="rounded bg-primary px-4 py-2 font-medium text-white disabled:opacity-50"
          disabled={!leadIdInput.trim()}
        >
          Load
        </button>
      </form>

      {leadId && isLoading ? <div className="text-text-secondary">Loading executions…</div> : null}
      {leadId && isError ? (
        <div className="text-danger">Failed to load executions for this lead.</div>
      ) : null}
      {leadId && !isLoading && !isError && executions.length === 0 ? (
        <div className="text-text-secondary">No agent executions found for this lead.</div>
      ) : null}

      {executions.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-text-secondary">
              <tr>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Model</th>
                <th className="px-4 py-2">Trigger</th>
                <th className="px-4 py-2">Duration</th>
                <th className="px-4 py-2">When</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((exec) => (
                <tr
                  key={exec.id}
                  className="cursor-pointer border-t border-border hover:bg-surface"
                  onClick={() => setSelectedId(exec.id)}
                >
                  <td className="px-4 py-2 text-text">{exec.agentType}</td>
                  <td className="px-4 py-2">
                    <Badge variant={statusVariant(exec.status)}>{exec.status}</Badge>
                  </td>
                  <td className="px-4 py-2">
                    {isFallback(exec) ? (
                      <Badge variant="warning">fallback</Badge>
                    ) : (
                      <span className="text-text-secondary">{exec.modelUsed ?? 'n/a'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-text-secondary">{exec.triggeredBy ?? '—'}</td>
                  <td className="px-4 py-2 text-text-secondary">
                    {exec.durationMs != null ? `${exec.durationMs} ms` : '—'}
                  </td>
                  <td className="px-4 py-2 text-text-secondary">
                    {new Date(exec.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Modal
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title="Execution detail"
        size="lg"
      >
        {selectedId ? <ExecutionDetail id={selectedId} /> : null}
      </Modal>
    </div>
  );
}
